/* eslint-disable no-unused-vars */
import React from "react";
import { Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { AccountStatusType } from "src/Enum/AccountStatus";
import { HeadCell } from "../Objects/interfaces/TableTypes";
import Teachr from "../Objects/models/Teachr";
import { getAllClients } from "../Redux/Actions/StudentActions";
import { capitalizeFirstLetter } from "../Utils/Function";
import { isVerifiedTrainer } from "../Utils/VerifiedTrainerAmounts";
import { getActiveClientBrandConfig, getActivePrestationBrandConfig } from "./environment.config";

const CoursesMonthHeader = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    📅 {capitalizeFirstLetter(getActivePrestationBrandConfig())} ce mois
    <Tooltip title="Le nombre maximum de cours déclarables par élève sur un mois calendaire est de 8." arrow>
      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }} />
    </Tooltip>
  </span>
);

const MontantHeader = () => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
    💰 Montant
    <Tooltip
      title={isVerifiedTrainer()
        ? "Prix total TTC déclaré sur le mois en cours (payé par le client + crédit d'impôt). Plafond : 1980 € par client et par mois."
        : `Montant déclaré sur le mois en cours.`}
      arrow
    >
      <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }} />
    </Tooltip>
  </span>
);

export type ClientType = 'clients';

export interface DispatchParams {
  teachr: Teachr;
  page: number;
  rowsPerPage: number;
  nameSearch: string;
  sort: { var: number; sort: number };
  archive?: number;
  validated?: number
  status?: AccountStatusType | AccountStatusType[];
}

export interface DispatchClientParams {
  teachrId: number,
  archive: number,
  page: number,
  variable: number,
  sort: number,
  limit: number,
  validated?: number,
  search?: string,
  onlyActive?: boolean,
  status?: AccountStatusType | AccountStatusType[];
}

export interface ClientTypeConfig {
  action: (arg: any) => any;
  stateKey: 'clients';
  dataKey: string;
  buildDispatch: (params: DispatchParams) => any;
  headCells: HeadCell[];
  emptyEmoji: string;
  emptyLabel: string;
  emptyLabelArchived?: string;
  showSort: boolean;
}


// ─── Head cells ───────────────────────────────────────────────────────────────

export const HEAD_CELLS_ACTIVE: HeadCell[] = [
  { id: 1, numeric: false, disable: false, align: 'left', label: `👤 ${capitalizeFirstLetter(getActiveClientBrandConfig())}` },
  { id: 2, numeric: false, disable: false, align: 'left', label: '📬 Contact' },
  { id: 3, numeric: false, disable: false, align: 'left', label: '⏱️ Durée' },
  { id: 4, numeric: false, disable: false, align: 'center', label: <MontantHeader /> },
  { id: 5, numeric: false, disable: false, align: 'center', label: <CoursesMonthHeader /> },
  { id: 6, numeric: false, disable: false, align: 'center', label: '✅ Statut' },
  { id: 7, numeric: false, disable: false, align: 'center', label: '⚙️ Actions' },
];

// ─── Options de tri ────────────────────────────────────────────────────────────

export const SORT_OPTIONS = [
  { value: '1-0', label: 'Trier par défaut', sort: 1, var: 0 },
  { value: '1-1', label: 'Montant croissant', sort: 1, var: 1 },
  { value: '0-1', label: 'Montant décroissant', sort: 0, var: 1 },
  { value: '1-2', label: 'Durée croissante', sort: 1, var: 2 },
  { value: '0-2', label: 'Durée décroissante', sort: 0, var: 2 },
];

// ─── Config ───────────────────────────────────────────────────────────────────

export const CLIENT_TYPE_CONFIG: Record<ClientType, ClientTypeConfig> = {
  clients: {
    action: getAllClients,
    stateKey: 'clients',
    dataKey: 'data',
    buildDispatch: ({ teachr, page, rowsPerPage, nameSearch, sort, archive, validated, status }) => ({
      teachrId: teachr?.id,
      archive: archive ?? 0,
      page: page,
      variable: sort?.var ?? 0,
      sort: sort?.sort ?? 1,
      limit: rowsPerPage,
      search: nameSearch || undefined,
      validated: validated,
      status: status,
    }),
    headCells: HEAD_CELLS_ACTIVE,
    emptyEmoji: '👥',
    emptyLabel: `Aucun ${getActiveClientBrandConfig()} trouvé`,
    emptyLabelArchived: `Aucun ${getActiveClientBrandConfig()} archivé trouvé`,
    showSort: true,
  },
};
